// src/stores/authStore.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService, User } from '../api';

// Define types
interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<boolean>;
  loadUserProfile: () => Promise<User | null>;
  setTokens: (accessToken: string, refreshToken: string) => void;
  logout: () => void;
  clearError: () => void;
}

// Create auth store with Zustand
const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      
      // Login action
      login: async (username: string, password: string) => {
        set({ isLoading: true, error: null });
        
        try {
          // First authenticate to get tokens
          const response = await authService.login({ username, password });
          const { access_token, refresh_token } = response.data;
          
          // Set tokens but don't mark as authenticated yet
          set({
            accessToken: access_token,
            refreshToken: refresh_token,
            isLoading: true,
          });
          
          // Load user profile with the new token
          const user = await get().loadUserProfile();
          
          // Check if the user has web access
          if (!user || user.webAllowed === false) {
            // User doesn't have web access permission
            set({
              accessToken: null,
              refreshToken: null,
              isAuthenticated: false,
              isLoading: false,
              error: 'Unauthorized',
              user: null
            });
            return false;
          }
          
          // User has web access, complete login
          set({
            isAuthenticated: true,
            isLoading: false,
            error: null
          });
          
          return true;
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.response?.data?.message || 'Login failed',
            isAuthenticated: false,
            accessToken: null,
            refreshToken: null
          });
          return false;
        }
      },
      
      // Load user profile
      loadUserProfile: async () => {
        try {          
          const response = await authService.getProfile();
          const user = response.data.data;
          set({ user });
          return user;
        } catch (error) {
          console.error('Failed to load user profile', error);
          return null;
        }
      },
      
      // Set tokens manually
      setTokens: (accessToken: string, refreshToken: string) => {
        set({
          accessToken,
          refreshToken,
          isAuthenticated: true
        });
      },
      
      // Logout action
      logout: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null
        });
        
        // Clear any potential redirect data from localStorage
        // This prevents the router from trying to redirect to protected routes
        localStorage.removeItem('router-last-location');
        localStorage.removeItem('auth-storage');
      },
      
      // Clear error
      clearError: () => set({ error: null })
    }),
    {
      name: 'auth-storage',
      // Only persist these fields
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
);

// Export as window property for debugging
if (typeof window !== 'undefined' && import.meta.env.MODE === 'development') {
  (window as any).authStore = useAuthStore;
}

export default useAuthStore;